import React, { useState, useEffect } from 'react';
import { inventoryLocationsApi } from '../api/locationsApi';
import BatchSelector from './BatchSelector';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Divider,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Inventory as InventoryIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enqueueSnackbar, useSnackbar } from 'notistack';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  ItemInventoryFormData, 
  ItemInventoryDetailFormData, 
  TRANSACTION_TYPES,
  TransactionTypeConfig 
} from '../types/inventory.types';
import { Item } from '../../settings/items/types/item.types';
import { ItemBatch } from '../../settings/items/types/item-batch.types';
import { ItemLocation, Location } from '../../settings/items/types/item-location.types';
import { inventoryApi } from '../api/inventoryApi';
import { itemApi } from '../../settings/items/api/itemApi';
import { itemBatchApi } from '../../settings/items/api/itemBatchApi';
import { itemLocationApi } from '../../settings/items/api/itemLocationApi';

interface InventoryFormProps {
  mode: 'create' | 'edit';
}

const InventoryForm: React.FC<InventoryFormProps> = ({ mode }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const transactionType = searchParams.get('type') as any;

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [items, setItems] = useState<Item[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [availableBatches, setAvailableBatches] = useState<ItemBatch[]>([]);
  const [currentStock, setCurrentStock] = useState<ItemLocation[]>([]);
  const [itemStocks, setItemStocks] = useState<Record<string, ItemLocation[]>>({});
  const [itemBatches, setItemBatches] = useState<Record<string, ItemBatch[]>>({});

  // Get transaction type configuration
  const typeConfig = TRANSACTION_TYPES.find(t => t.type === transactionType) || TRANSACTION_TYPES[0];

  const [formData, setFormData] = useState<ItemInventoryFormData>({
    transactionDate: new Date().toISOString(),
    type: transactionType || 'INCOMING',
    fromLocationId: '',
    toLocationId: '',
    remarks: '',
    status: 'DRAFT',
    details: []
  });

  // Load initial data
  useEffect(() => {
    loadItems();
    loadLocations();
    if (mode === 'edit' && id) {
      loadInventoryTransaction();
    }
  }, [mode, id]);

// Load items (inventory type only)
const loadItems = async () => {
    try {
      console.log('🔍 Loading items with itemType=INVENTORY...');
      
      let allItems: Item[] = [];
      let currentPage = 1;
      let totalPages = 1;
      
      // Fetch all pages
      do {
        const response = await itemApi.getItems({ 
          limit: 100,
          page: currentPage 
        });
        
        console.log(`📦 Page ${currentPage}/${response.totalPages}: Loaded ${response.data.length} items`);
        
        allItems = [...allItems, ...response.data];
        totalPages = response.totalPages;
        currentPage++;
      } while (currentPage <= totalPages);
      
      console.log('✅ Total items loaded:', allItems.length);
      
      // Filter for inventory items only
      const inventoryItems = allItems.filter(item => {
        return item.itemType === 'INVENTORY';
      });
      console.log('🎯 Filtered inventory items:', inventoryItems.length, 'out of', allItems.length, 'total items');
      
      if (inventoryItems.length > 0) {
        console.log('✅ Inventory items found:', inventoryItems.length, 'items');
      }
      
      setItems(inventoryItems);
      
      if (inventoryItems.length === 0) {
        console.log('⚠️ No inventory items found. Please create items with itemType=INVENTORY');
        enqueueSnackbar('No inventory items found. Please create items with itemType=INVENTORY', { variant: 'warning' });
      }
    } catch (error) {
      console.error('❌ Error loading items:', error);
      enqueueSnackbar('Failed to load items', { variant: 'error' });
    }
  };

// Load locations
const loadLocations = async () => {
    try {
      const data = await inventoryLocationsApi.getAllActiveLocations();
      setLocations(data);
    } catch (error) {
      console.error('Error loading locations:', error);
      enqueueSnackbar('Failed to load locations', { variant: 'error' });
    }
  };

  // Load inventory transaction for editing
  const loadInventoryTransaction = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const transaction = await inventoryApi.getInventoryTransactionById(id);
      
      setFormData({
        transactionDate: transaction.transactionDate,
        type: transaction.type,
        fromLocationId: transaction.fromLocationId || '',
        toLocationId: transaction.toLocationId || '',
        remarks: transaction.remarks || '',
        status: transaction.status,
        details: transaction.details?.map(detail => ({
          itemId: detail.itemId,
          itemLocationId: detail.itemLocationId || '',
          batchNumber: detail.batchNumber,
          expiryDate: detail.expiryDate || '',
          quantity: detail.quantity,
          costPrice: detail.costPrice,
          mode: detail.mode,
          deductedFromId: detail.deductedFromId || ''
        })) || []
      });
    } catch (error) {
      console.error('Error loading inventory transaction:', error);
      enqueueSnackbar('Failed to load inventory transaction', { variant: 'error' });
      navigate('/inventory/transactions');
    } finally {
      setLoading(false);
    }
  };

  // Load batches for selected item
  const loadBatchesForItem = async (itemId: string): Promise<ItemBatch[]> => {
    try {
      console.log('🔍 Loading batches for item:', itemId);
      console.log('🔍 Current form data:', formData);
      
      // First, load all batches for this item
      const batches = await itemBatchApi.getItemBatchesByItemId(itemId);
      console.log('✅ All batches loaded:', batches);
      
      // For incoming transactions (RECEIVED, RETURNED, ADJUSTMENT_IN, TRANSFER_IN), show all batches
      const isIncoming = [
        'RECEIVED', 
        'RETURNED', 
        'ADJUSTMENT_IN',
        'TRANSFER_IN'
      ].includes(formData.type);
      
      // For outgoing transactions (DISPOSED, DISPENSED_TO_PATIENT, TRANSFER), only show batches with stock
      const isOutgoing = [
        'DISPOSED',
        'DISPENSED_TO_PATIENT',
        'TRANSFER'
      ].includes(formData.type);
      
      console.log('🔄 Transaction type:', formData.type, 'isIncoming:', isIncoming, 'isOutgoing:', isOutgoing);
      
      let filteredBatches = batches;
      
      // For outgoing transactions, only show batches with available stock
      if (isOutgoing) {
        // Get the relevant location ID based on transaction type
        const locationId = getRelevantLocationId();
        console.log('🏢 Relevant location ID for filtering batches:', locationId);
        
        if (locationId) {
          try {
            // Load all stock for this item directly from API to ensure fresh data
            console.log('🔍 Fetching fresh stock data for item:', itemId);
            const allStock = await itemLocationApi.getItemLocationsByItemId(itemId);
            console.log('✅ Fresh stock data loaded:', allStock);
            
            // Update the stock in state
            setItemStocks(prev => ({
              ...prev,
              [itemId]: allStock
            }));
            
            // Filter stock for the current location and positive balance
            const locationStock = allStock.filter(
              stock => stock.locationId === locationId && stock.balance > 0
            );
            console.log('📊 Filtered stock with positive balance:', locationStock);
            
            // Debug: Check if locationId matches any stock items
            const anyMatchingLocation = allStock.some(stock => stock.locationId === locationId);
            console.log('🔍 Any stock with matching locationId:', anyMatchingLocation);
            
            // Debug: Check if any stock has positive balance
            const anyPositiveBalance = allStock.some(stock => stock.balance > 0);
            console.log('🔍 Any stock with positive balance:', anyPositiveBalance);
            
            if (locationStock.length > 0) {
              // Get batch numbers with stock at this location
              const batchesWithStock = [...new Set(locationStock.map(stock => stock.batchNumber))];
              console.log('✅ Batches with stock at location:', batchesWithStock);
              
              // Filter the batches to only include those with stock
              filteredBatches = batches.filter(batch => 
                batchesWithStock.includes(batch.batchNumber)
              );
              
              console.log('📦 Filtered batches with stock:', filteredBatches);
              
              // If we still have no batches after filtering, this is likely an error
              // For testing purposes, let's fall back to showing all batches
              if (filteredBatches.length === 0 && batches.length > 0) {
                console.log('⚠️ No batches after filtering but batches exist. Showing all batches for debugging.');
                filteredBatches = batches;
              }
            } else {
              // If no stock at all, return empty array
              console.log('⚠️ No stock available at this location');
              filteredBatches = [];
            }
          } catch (error) {
            console.error('❌ Error loading stock data:', error);
            // For now, show all batches if there's an error loading stock
            filteredBatches = batches;
          }
        } else {
          console.log('⚠️ No relevant location ID found for filtering');
          // If no location is selected, show all batches for now
          filteredBatches = batches;
        }
      } else {
        console.log('📦 Showing all batches for incoming/other transaction');
      }
      
      // Store batches for this specific item
      setItemBatches(prev => ({
        ...prev,
        [itemId]: filteredBatches
      }));
      
      // Also update current batches for immediate display
      setAvailableBatches(filteredBatches);
      return filteredBatches;
    } catch (error) {
      console.error('❌ Error loading batches:', error);
      setItemBatches(prev => ({
        ...prev,
        [itemId]: []
      }));
      setAvailableBatches([]);
      return [];
    }
  };

  // Load current stock for item at specific location and batch
  const loadCurrentStock = async (itemId: string, locationId?: string, batchNumber?: string) => {
    try {
      console.log('🔍 Loading current stock for:', { itemId, locationId, batchNumber });
      
      let stockData: ItemLocation[] = [];
      
      if (locationId && batchNumber) {
        // Check for specific item + location + batch combination
        console.log('🔍 Fetching specific stock for item, location, and batch:', { itemId, locationId, batchNumber });
        const specificStock = await itemLocationApi.getItemLocationByItemLocationBatch(itemId, locationId, batchNumber);
        console.log('✅ Specific stock found:', specificStock);
        stockData = specificStock ? [specificStock] : [];
      } else if (locationId) {
        // Load stock for this item at the specific location only
        console.log('🔍 Fetching all stock for item:', itemId);
        const allStock = await itemLocationApi.getItemLocationsByItemId(itemId);
        console.log('🔍 All stock before filtering:', allStock);
        
        const locationStock = allStock.filter(stock => stock.locationId === locationId);
        console.log('✅ Location-specific stock loaded for location', locationId, ':', locationStock);
        console.log('🔍 Stock with positive balance:', locationStock.filter(stock => stock.balance > 0));
        stockData = locationStock;
      } else {
        // Load all stock for this item (fallback)
        console.log('🔍 Fetching all stock for item (no location specified):', itemId);
        const allStock = await itemLocationApi.getItemLocationsByItemId(itemId);
        console.log('✅ All stock loaded:', allStock);
        stockData = allStock;
      }
      
      // Store stock for this specific item
      setItemStocks(prev => ({
        ...prev,
        [itemId]: stockData
      }));
      
      // Also update current stock for immediate display
      setCurrentStock(stockData);
    } catch (error) {
      console.error('❌ Error loading current stock:', error);
      setCurrentStock([]);
      setItemStocks(prev => ({
        ...prev,
        [itemId]: []
      }));
    }
  };

  // Handle detail change
  const handleDetailChange = (index: number, field: keyof ItemInventoryDetailFormData, value: any) => {
    // Real-time quantity validation for stock-reducing transactions
    if (field === 'quantity') {
      const currentDetail = formData.details[index];
      
      // Check if this is an outgoing transaction type
      const isOutgoing = [
        'DISPOSED',
        'DISPENSED_TO_PATIENT',
        'TRANSFER'
      ].includes(formData.type);
      
      // Only validate stock for outgoing transactions
      if (isOutgoing && currentDetail.itemId && currentDetail.batchNumber && value > 0) {
        const locationId = getRelevantLocationId();
        const availableStock = getStockForItem(currentDetail.itemId);
        
        // Find stock for this specific batch at this location
        const batchStock = availableStock.find(
          stock => stock.batchNumber === currentDetail.batchNumber && 
                  stock.locationId === locationId && 
                  stock.balance > 0
        );
        
        console.log('🔍 Validating quantity:', { 
          value, 
          batchStock, 
          availableBalance: batchStock?.balance || 0 
        });
        
        if (batchStock && value > batchStock.balance) {
          // For outgoing transactions, prevent exceeding available stock
          enqueueSnackbar(
            `Quantity (${value}) exceeds available stock (${batchStock.balance}) for this batch`, 
            { variant: 'error' }
          );
          
          // Limit the value to the available stock
          value = batchStock.balance;
          
          console.log('⚠️ Quantity limited to available stock:', value);
        } else if (!batchStock && value > 0) {
          // No stock available for this batch at this location
          enqueueSnackbar(
            `No stock available for this batch at the selected location`, 
            { variant: 'error' }
          );
          
          // Reset to 0
          value = 0;
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      details: prev.details.map((detail, i) => 
        i === index ? { ...detail, [field]: value } : detail
      )
    }));
  };

  // Handle item selection and load its batches
  const handleItemSelection = async (index: number, itemId: string) => {
    // Update the item ID
    handleDetailChange(index, 'itemId', itemId);
    
    // Load batches for this item
    if (itemId) {
      const batches = await loadBatchesForItem(itemId);
      
      // Load stock for this item at the relevant location
      const relevantLocationId = getRelevantLocationId();
      await loadCurrentStock(itemId, relevantLocationId || undefined);
      
      // If no batches found, you might want to show a message or icon
      if (batches.length === 0) {
        console.log('⚠️ No batches found for item:', itemId);
      }
    } else {
      // Clear batches and stock when no item is selected
      setAvailableBatches([]);
      setCurrentStock([]);
    }
  };

  // Handle batch selection and check specific stock
  const handleBatchSelection = async (index: number, batchNumber: string, expiryDate?: string) => {
    console.log('🔍 handleBatchSelection - index:', index, 'batchNumber:', batchNumber, 'expiryDate:', expiryDate);
    
    // Update batch details
    handleDetailChange(index, 'batchNumber', batchNumber);
    if (expiryDate) {
      handleDetailChange(index, 'expiryDate', expiryDate);
    }

    // Get the current detail to access itemId
    const currentDetail = formData.details[index];
    if (currentDetail.itemId && batchNumber) {
      // Check if this is a manually entered batch (not in availableBatches)
      const isManualEntry = !getBatchesForItem(currentDetail.itemId).some(b => b.batchNumber === batchNumber);
      console.log('🔍 Is manual batch entry:', isManualEntry);
      
      // Determine the location to check based on transaction type
      const locationId = getRelevantLocationId();
      console.log('🔍 Checking stock at location:', locationId);
      
      if (locationId) {
        // Check if ItemLocation exists for this combination
        await loadCurrentStock(currentDetail.itemId, locationId, batchNumber);
        
        // For outgoing transactions with manual batch entry, check if stock exists
        const isOutgoing = ['DISPOSED', 'DISPENSED_TO_PATIENT', 'TRANSFER'].includes(formData.type);
        if (isOutgoing && isManualEntry) {
          // Fetch fresh stock data for this item and location
          try {
            console.log('🔍 Fetching fresh stock data for manual batch check');
            const allStock = await itemLocationApi.getItemLocationsByItemId(currentDetail.itemId);
            
            // Update the stock in state
            setItemStocks(prev => ({
              ...prev,
              [currentDetail.itemId]: allStock
            }));
            
            // Check if this batch has stock at this location
            const batchStock = allStock.find(
              stock => stock.batchNumber === batchNumber && 
                      stock.locationId === locationId && 
                      stock.balance > 0
            );
            
            if (!batchStock) {
              console.log('⚠️ No stock found for manually entered batch:', batchNumber);
              enqueueSnackbar(`No stock found for batch ${batchNumber} at this location`, { variant: 'warning' });
              
              // For outgoing transactions, we should warn the user that they can't use batches without stock
              enqueueSnackbar(
                `For ${formData.type} transactions, you can only use batches with available stock.`, 
                { variant: 'info' }
              );
            } else {
              console.log('✅ Stock found for manually entered batch:', batchStock);
              enqueueSnackbar(
                `Available stock for batch ${batchNumber}: ${batchStock.balance}`, 
                { variant: 'success' }
              );
            }
          } catch (error) {
            console.error('❌ Error checking stock for manual batch:', error);
          }
        }
      }
    }
  };

  // Get the relevant location ID based on transaction type
  const getRelevantLocationId = (): string | null => {
    console.log('🔍 getRelevantLocationId - Transaction type:', formData.type);
    console.log('🔍 getRelevantLocationId - fromLocationId:', formData.fromLocationId);
    console.log('🔍 getRelevantLocationId - toLocationId:', formData.toLocationId);
    
    let locationId: string | null = null;
    
    switch (formData.type) {
      case 'INCOMING':
      case 'ADJUSTMENT':
        locationId = formData.toLocationId || null; // Items coming INTO this location
        break;
      case 'DISPENSED_TO_PATIENT':
      case 'DISPOSED':
        locationId = formData.fromLocationId || null; // Items going OUT of this location
        break;
      case 'TRANSFER':
      case 'IN_WARD':
        // For transfers, we need to handle both locations
        // For now, return fromLocationId (where items are coming from)
        locationId = formData.fromLocationId || null;
        break;
      default:
        locationId = null;
    }
    
    console.log('🔍 getRelevantLocationId - Selected locationId:', locationId);
    return locationId;
  };

  // Handle form field change
  const handleFieldChange = (field: keyof ItemInventoryFormData, value: any) => {
    // Check if location is changing and there are items in the grid
    if ((field === 'fromLocationId' || field === 'toLocationId') && formData.details.length > 0) {
      const currentValue = formData[field];
      if (currentValue && currentValue !== value) {
        const locationName = field === 'fromLocationId' ? 'From Location' : 'To Location';
        const shouldContinue = window.confirm(
          `Changing the ${locationName} will clear all items from the grid as stock balances may differ. Do you want to continue?`
        );
        
        if (shouldContinue) {
          // Clear the details and update the location
          setFormData(prev => ({
            ...prev,
            [field]: value,
            details: [] // Clear all items
          }));
          enqueueSnackbar(`${locationName} changed. Please re-add items to see updated stock balances.`, { 
            variant: 'info' 
          });
          return;
        } else {
          // Don't change the location
          return;
        }
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Add new detail row
  const addDetailRow = () => {
    const newDetail: ItemInventoryDetailFormData = {
      itemId: '',
      itemLocationId: '',
      batchNumber: '',
      expiryDate: '',
      quantity: 0,
      costPrice: (formData.type === 'DISPOSED' || formData.type === 'DISPENSED_TO_PATIENT' || formData.type === 'TRANSFER') ? 0 : 0, // Auto-calculated for disposal and transfer
      mode: typeConfig.defaultMode,
      deductedFromId: ''
    };

    setFormData(prev => ({
      ...prev,
      details: [...prev.details, newDetail]
    }));
  };

  // Remove detail row
  const removeDetailRow = (index: number) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index)
    }));
  };

  // Get stock for specific item
  const getStockForItem = (itemId: string): ItemLocation[] => {
    return itemStocks[itemId] || [];
  };

  // Get batches for specific item
  const getBatchesForItem = (itemId: string): ItemBatch[] => {
    return itemBatches[itemId] || [];
  };
  
  // Get available stock for a specific batch at the relevant location
  const getAvailableStockForBatch = (itemId: string, batchNumber: string): number => {
    if (!itemId || !batchNumber) return 0;
    
    const locationId = getRelevantLocationId();
    if (!locationId) return 0;
    
    const stockForItem = getStockForItem(itemId);
    console.log('🔍 getAvailableStockForBatch - itemId:', itemId, 'batchNumber:', batchNumber, 'locationId:', locationId);
    console.log('🔍 getAvailableStockForBatch - stockForItem:', stockForItem);
    
    // Find stock for this specific batch at this location
    const batchStock = stockForItem.find(
      stock => stock.batchNumber === batchNumber && 
              stock.locationId === locationId && 
              stock.balance > 0
    );
    
    const availableStock = batchStock ? batchStock.balance : 0;
    console.log('🔍 getAvailableStockForBatch - batchStock:', batchStock, 'availableStock:', availableStock);
    
    // For outgoing transactions, we need to check if this is a valid batch
    const isOutgoing = ['DISPOSED', 'DISPENSED_TO_PATIENT', 'TRANSFER'].includes(formData.type);
    
    // If this is an outgoing transaction and the batch was manually entered but has no stock,
    // we should show a warning
    if (isOutgoing && batchNumber && availableStock === 0) {
      // Check if this is a manually entered batch (not in availableBatches)
      const isManualEntry = !getBatchesForItem(itemId).some(b => b.batchNumber === batchNumber);
      
      if (isManualEntry) {
        console.log('⚠️ Manual batch entry with no stock for outgoing transaction:', batchNumber);
      }
    }
    
    return availableStock;
  };

  // Handle location change and refresh stock for all items
  const handleLocationChange = (field: 'fromLocationId' | 'toLocationId', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Refresh stock for all selected items with the new location
    formData.details.forEach((detail) => {
      if (detail.itemId) {
        const relevantLocationId = getRelevantLocationId();
        loadCurrentStock(detail.itemId, relevantLocationId || undefined);
      }
    });
  };

  // Calculate total value
  const calculateTotalValue = () => {
    return formData.details.reduce((sum, detail) => sum + (detail.quantity * detail.costPrice), 0);
  };

  // Handle save
  const handleSave = async (status: 'DRAFT' | 'APPROVED' = 'DRAFT') => {
    try {
      setSaving(true);

      if (formData.details.length === 0) {
        enqueueSnackbar('Please add at least one item detail', { variant: 'error' });
        return;
      }

      // Validate required fields based on transaction type
      if (!formData.toLocationId && (formData.type === 'INCOMING' || formData.type === 'ADJUSTMENT' || formData.type === 'TRANSFER' || formData.type === 'IN_WARD')) {
        enqueueSnackbar('Please select a TO location', { variant: 'error' });
        return;
      }

      if (!formData.fromLocationId && (formData.type === 'DISPENSED_TO_PATIENT' || formData.type === 'DISPOSED' || formData.type === 'TRANSFER' || formData.type === 'IN_WARD')) {
        enqueueSnackbar('Please select a FROM location', { variant: 'error' });
        return;
      }

      // Validate that fromLocation and toLocation are different for TRANSFER
      if (formData.type === 'TRANSFER' && formData.fromLocationId === formData.toLocationId) {
        enqueueSnackbar('From Location and To Location cannot be the same for Transfer transactions', { variant: 'error' });
        return;
      }

      // Validate item details
      for (let i = 0; i < formData.details.length; i++) {
        const detail = formData.details[i];
        if (!detail.itemId) {
          enqueueSnackbar(`Please select an item for row ${i + 1}`, { variant: 'error' });
          return;
        }
        if (!detail.batchNumber) {
          enqueueSnackbar(`Please enter a batch number for row ${i + 1}`, { variant: 'error' });
          return;
        }
        if (detail.quantity <= 0) {
          enqueueSnackbar(`Please enter a valid quantity for row ${i + 1}`, { variant: 'error' });
          return;
        }
        
        // For outgoing transactions (DISPOSED/DISPENSED/TRANSFER), validate against available stock
        const isOutgoing = [
          'DISPOSED',
          'DISPENSED_TO_PATIENT',
          'TRANSFER'
        ].includes(formData.type);
        
        if (isOutgoing && detail.itemId && detail.batchNumber) {
          // Get available stock for this batch at the relevant location
          const availableStock = getAvailableStockForBatch(detail.itemId, detail.batchNumber);
          
          if (availableStock <= 0) {
            enqueueSnackbar(`No stock available for batch ${detail.batchNumber} in row ${i + 1}`, { variant: 'error' });
            setSaving(false);
            return;
          }
          
          if (detail.quantity > availableStock) {
            enqueueSnackbar(`Quantity (${detail.quantity}) exceeds available stock (${availableStock}) for batch ${detail.batchNumber} in row ${i + 1}`, { variant: 'error' });
            setSaving(false);
            return;
          }
        }
        // Skip cost price validation for DISPOSED, DISPENSED and TRANSFER transactions (auto-calculated)
        if (detail.costPrice <= 0 && formData.type !== 'DISPOSED' && formData.type !== 'DISPENSED_TO_PATIENT' && formData.type !== 'TRANSFER') {
          enqueueSnackbar(`Please enter a valid cost price for row ${i + 1}`, { variant: 'error' });
          return;
        }
      }

      // Prepare the data with proper location logic
      const preparedData = {
        ...formData,
        // Set the status based on the button clicked
        status: status,
        // For INCOMING and ADJUSTMENT, both from and to should be the same location
        fromLocationId: formData.type === 'INCOMING' || formData.type === 'ADJUSTMENT' 
          ? formData.toLocationId || null
          : formData.fromLocationId || null,
        toLocationId: formData.toLocationId || null,
        details: formData.details.map(detail => {
          const preparedDetail: any = {
            itemId: detail.itemId,
            batchNumber: detail.batchNumber,
            quantity: detail.quantity,
            costPrice: detail.costPrice,
            mode: detail.mode
          };

          // Only include optional fields if they have valid values
          if (detail.expiryDate && detail.expiryDate.trim() !== '') {
            preparedDetail.expiryDate = detail.expiryDate;
          }

          // For itemLocationId, we need to either provide a valid UUID or omit it
          // For now, we'll omit it since we don't have a location selection mechanism
          if (detail.itemLocationId && detail.itemLocationId.trim() !== '') {
            preparedDetail.itemLocationId = detail.itemLocationId;
          }

          // deductedFromId is only needed for DEBIT transactions that reference another transaction
          if (detail.deductedFromId && detail.deductedFromId.trim() !== '') {
            preparedDetail.deductedFromId = detail.deductedFromId;
          }

          return preparedDetail;
        })
      };

      console.log('🚀 Prepared data for submission:', preparedData);
      console.log('📋 Transaction details:', {
        type: preparedData.type,
        fromLocationId: preparedData.fromLocationId,
        toLocationId: preparedData.toLocationId,
        detailsCount: preparedData.details.length,
        firstDetail: preparedData.details[0]
      });

      if (mode === 'create') {
        await inventoryApi.createInventoryTransaction(preparedData);
        const message = status === 'APPROVED' 
          ? 'Inventory transaction created and approved successfully! Stock balances updated.' 
          : 'Inventory transaction saved as draft successfully';
        enqueueSnackbar(message, { variant: 'success' });
      } else {
        await inventoryApi.updateInventoryTransaction(id!, preparedData);
        const message = status === 'APPROVED' 
          ? 'Inventory transaction updated and approved successfully! Stock balances updated.' 
          : 'Inventory transaction updated successfully';
        enqueueSnackbar(message, { variant: 'success' });
      }

      navigate('/inventory/transactions');
    } catch (error: any) {
      console.error('Error saving inventory transaction:', error);
      enqueueSnackbar(error.message || 'Failed to save inventory transaction', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Helper function to generate a placeholder UUID (temporary solution)
  const generatePlaceholderUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  // Handle cancel
  const handleCancel = () => {
    navigate('/inventory/transactions');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <InventoryIcon color="primary" />
            <Typography variant="h4">
              {mode === 'create' ? 'New' : 'Edit'} {typeConfig.label}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<CancelIcon />}
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SaveIcon />}
              onClick={() => handleSave('DRAFT')}
              disabled={saving}
              sx={{ mr: 1 }}
            >
              {saving ? 'Saving...' : 'Save as Draft'}
            </Button>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              onClick={() => handleSave('APPROVED')}
              disabled={saving}
            >
              {saving ? 'Saving & Approving...' : 'Save & Approve'}
            </Button>
          </Box>
        </Box>

        {/* Transaction Type Info */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>{typeConfig.label}:</strong> {typeConfig.description}
          </Typography>
        </Alert>

        {/* Basic Information */}
        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Transaction Information
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <DateTimePicker
                label="Transaction Date"
                value={new Date(formData.transactionDate)}
                onChange={(date) => handleFieldChange('transactionDate', date?.toISOString())}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    required: true
                  }
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                select
                fullWidth
                label="Transaction Type"
                value={formData.type}
                onChange={(e) => handleFieldChange('type', e.target.value)}
                required
                disabled={mode === 'edit' || !!transactionType}
              >
                {TRANSACTION_TYPES.map((type) => (
                  <MenuItem key={type.type} value={type.type}>
                    {type.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {typeConfig.requiresFromLocation && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={locations}
                  getOptionLabel={(option) => option.name}
                  value={locations.find(l => l.id === formData.fromLocationId) || null}
                  onChange={(_, value) => handleFieldChange('fromLocationId', value?.id || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="From Location" required />
                  )}
                />
              </Grid>
            )}

            {typeConfig.requiresToLocation && (
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={locations}
                  getOptionLabel={(option) => option.name}
                  value={locations.find(l => l.id === formData.toLocationId) || null}
                  onChange={(_, value) => handleFieldChange('toLocationId', value?.id || '')}
                  renderInput={(params) => (
                    <TextField {...params} label="To Location" required />
                  )}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Remarks"
                value={formData.remarks}
                onChange={(e) => handleFieldChange('remarks', e.target.value)}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </Paper>

        {/* Item Details */}
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">
              Item Details
            </Typography>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={addDetailRow}
            >
              Add Item
            </Button>
          </Box>
          <Divider sx={{ mb: 3 }} />

          {formData.details.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Typography variant="body2" color="textSecondary">
                No items added. Click "Add Item" to start.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Item</TableCell>
                    <TableCell>Batch Number</TableCell>
                    <TableCell>Expiry Date</TableCell>
                    <TableCell>Quantity</TableCell>
                    <TableCell>Cost Price</TableCell>
                    <TableCell>Total Value</TableCell>
                    <TableCell>Current Stock</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.details.map((detail, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Autocomplete
                          options={items}
                          getOptionLabel={(option) => `${option.itemCode} - ${option.itemName}`}
                          value={items.find(i => i.id === detail.itemId) || null}
                          onChange={(_, value) => handleItemSelection(index, value?.id || '')}
                          renderInput={(params) => (
                            <TextField {...params} size="small" required placeholder="Search items..." />
                          )}
                          sx={{ minWidth: 200 }}
                        />
                      </TableCell>
                      <TableCell>
                        <BatchSelector
                          itemId={detail.itemId}
                          availableBatches={getBatchesForItem(detail.itemId)}
                          selectedBatchNumber={detail.batchNumber}
                          transactionType={formData.type}
                          onBatchChange={(batchNumber, expiryDate) => {
                            handleBatchSelection(index, batchNumber, expiryDate);
                          }}
                          onBatchesUpdated={(batches) => {
                            // Update batches for this specific item
                            setItemBatches(prev => ({
                              ...prev,
                              [detail.itemId]: batches
                            }));
                          }}
                          disabled={!detail.itemId}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="date"
                          size="small"
                          value={detail.expiryDate ? detail.expiryDate.split('T')[0] : ''}
                          onChange={(e) => handleDetailChange(index, 'expiryDate', e.target.value)}
                          sx={{ minWidth: 150 }}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Check if this is an outgoing transaction type */}
                        {['DISPOSED', 'DISPENSED_TO_PATIENT', 'TRANSFER'].includes(formData.type) && 
                         detail.itemId && detail.batchNumber ? (
                          <Box>
                            <TextField
                              type="number"
                              size="small"
                              value={detail.quantity}
                              onChange={(e) => handleDetailChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                              required
                              error={detail.quantity < 0 || detail.quantity > getAvailableStockForBatch(detail.itemId, detail.batchNumber)}
                              helperText={
                                detail.quantity > getAvailableStockForBatch(detail.itemId, detail.batchNumber) ?
                                "Exceeds available stock" : ""
                              }
                              InputProps={{
                                inputProps: { 
                                  min: 0,
                                  max: getAvailableStockForBatch(detail.itemId, detail.batchNumber)
                                }
                              }}
                              sx={{ width: 100 }}
                            />
                            {/* Show available stock for this batch */}
                            <Typography 
                              variant="caption" 
                              color={detail.quantity > getAvailableStockForBatch(detail.itemId, detail.batchNumber) ? "error" : "textSecondary"} 
                              display="block"
                            >
                              Available: {getAvailableStockForBatch(detail.itemId, detail.batchNumber)}
                            </Typography>
                          </Box>
                        ) : (
                          <TextField
                            type="number"
                            size="small"
                            value={detail.quantity}
                            onChange={(e) => handleDetailChange(index, 'quantity', parseFloat(e.target.value) || 0)}
                            required
                            error={detail.quantity < 0}
                            InputProps={{
                              inputProps: { min: 0 }
                            }}
                            sx={{ width: 100 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {formData.type === 'DISPOSED' || formData.type === 'DISPENSED_TO_PATIENT' || formData.type === 'TRANSFER' ? (
                          <Typography variant="body2" color="textSecondary">
                            Auto-calculated
                          </Typography>
                        ) : (
                          <TextField
                            type="number"
                            size="small"
                            value={detail.costPrice}
                            onChange={(e) => handleDetailChange(index, 'costPrice', parseFloat(e.target.value) || 0)}
                            required
                            sx={{ width: 100 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formData.type === 'DISPOSED' || formData.type === 'DISPENSED_TO_PATIENT' || formData.type === 'TRANSFER' ? (
                            <Typography variant="body2" color="textSecondary">
                              Auto-calculated
                            </Typography>
                          ) : (
                            `${(detail.quantity * detail.costPrice).toFixed(2)}`
                          )}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          {detail.itemId && getStockForItem(detail.itemId).length > 0 ? (
                            getStockForItem(detail.itemId).map(stock => (
                                <Box key={stock.id} sx={{ mb: 0.5 }}>
                                  <Chip
                                    label={`${stock.location?.name || 'Unknown'}: ${stock.balance}`}
                                    size="small"
                                    color={stock.balance > 0 ? 'success' : 'default'}
                                    sx={{ mr: 0.5 }}
                                  />
                                  {stock.batchNumber && (
                                    <Typography variant="caption" color="textSecondary" display="block">
                                      Batch: {stock.batchNumber}
                                    </Typography>
                                  )}
                                </Box>
                              ))
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              {detail.itemId && detail.batchNumber && getRelevantLocationId() 
                                ? 'New batch - will create stock record' 
                                : 'Select item and batch to check stock'
                              }
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeDetailRow(index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  
                  {/* Total Row */}
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={5} sx={{ fontWeight: 'bold' }}>
                      TOTAL
                    </TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>
                      {calculateTotalValue().toFixed(2)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};

export default InventoryForm;
